import { useEffect, useState } from "react";

interface UsePromptTimerOptions {
  durationSeconds: number;
  onComplete: () => void;
  startImmediately?: boolean;
}

export const usePromptTimer = ({
  durationSeconds,
  onComplete,
  startImmediately = true,
}: UsePromptTimerOptions) => {
  const [startedAtMs, setStartedAtMs] = useState<number | null>(() =>
    startImmediately ? Date.now() : null,
  );

  const [nowMs, setNowMs] = useState(() => Date.now());
  const [isRunning, setIsRunning] = useState(startImmediately);
  const [hasCompleted, setHasCompleted] = useState(false);

  const stop = () => {
    setIsRunning(false);
  };

  const reset = () => {
    const now = Date.now();

    setStartedAtMs(now);
    setNowMs(now);
    setHasCompleted(false);
    setIsRunning(true);
  };

  const elapsedSeconds =
    startedAtMs === null
      ? 0
      : Math.min(Math.floor((nowMs - startedAtMs) / 1000), durationSeconds);

  const remainingSeconds = Math.max(durationSeconds - elapsedSeconds, 0);

  useEffect(() => {
    if (!isRunning || startedAtMs === null || hasCompleted) return;

    const intervalId = window.setInterval(() => {
      setNowMs(Date.now());
    }, 1000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [isRunning, startedAtMs, hasCompleted]);

  useEffect(() => {
    if (!isRunning || startedAtMs === null || hasCompleted) return;

    const elapsedMs = Date.now() - startedAtMs;
    const remainingMs = Math.max(durationSeconds * 1000 - elapsedMs, 0);

    const timeoutId = window.setTimeout(() => {
      setHasCompleted(true);
      setIsRunning(false);
      onComplete();
    }, remainingMs);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [isRunning, startedAtMs, durationSeconds, onComplete, hasCompleted]);

  return {
    elapsedSeconds,
    remainingSeconds,
    isRunning,
    hasCompleted,
    stop,
    reset,
  };
};
